import { AuditLogChangeType, HistoryDetailsItem } from "@common/types/auditLog";
import { Account } from "@common/types/domains/accounting/account";
import { displayAccountLabel } from "@common/helpers/accounting";
import { SpreadsheetData } from "@components/spreadsheet/types";
import { getDiffEntry } from "../../utils/getDiffEntry";
import { journalEntryLineDataSchema } from "./schema";

interface ResolutionContext {
    accountsById: Record<string, Account>;
    showAccountNumbers: boolean;
    counterpartiesById: Map<string, string>;
    classesById: Map<string, string>;
}

interface ParsedLine {
    accountId: string;
    accountName: string;
    counterpartyId: string | null;
    counterpartyName?: string;
    classIds: string[];
    classLabel?: string;
    debitAmount: number | null;
    creditAmount: number | null;
    description: string;
    lineOrder: number;
}

function resolveAccountName(
    accountId: string,
    accountCode: number | undefined,
    ctx: ResolutionContext,
): string {
    const account = ctx.accountsById[accountId];
    if (account) {
        return displayAccountLabel(account, ctx.showAccountNumbers);
    }
    if (accountCode) {
        return String(accountCode);
    }
    return accountId;
}

function resolveCounterpartyName(
    counterpartyId: string | null | undefined,
    ctx: ResolutionContext,
): string | undefined {
    if (!counterpartyId) {
        return undefined;
    }
    return ctx.counterpartiesById.get(counterpartyId);
}

function resolveClassLabel(
    classIds: string[] | undefined,
    ctx: ResolutionContext,
): string | undefined {
    if (!classIds || classIds.length === 0) {
        return undefined;
    }
    return classIds
        .map((id) => ctx.classesById.get(id))
        .filter(Boolean)
        .join(", ");
}

function parseLineData(
    raw: Record<string, unknown>,
    index: number,
    ctx: ResolutionContext,
): ParsedLine {
    const parsed = journalEntryLineDataSchema.parse(raw);
    const lineOrder = parsed.lineOrder || index + 1;
    return {
        accountId: parsed.accountId,
        accountName: resolveAccountName(
            parsed.accountId,
            parsed.accountCode,
            ctx,
        ),
        counterpartyId: parsed.counterpartyId ?? null,
        counterpartyName: resolveCounterpartyName(parsed.counterpartyId, ctx),
        classIds: parsed.classIds ?? [],
        classLabel: resolveClassLabel(parsed.classIds, ctx),
        debitAmount: parsed.debitAmount,
        creditAmount: parsed.creditAmount,
        description: parsed.description,
        lineOrder,
    };
}

function parseLinesArray(val: unknown, ctx: ResolutionContext): ParsedLine[] {
    if (!val || !Array.isArray(val)) {
        return [];
    }
    return val.map((line: Record<string, unknown>, index: number) =>
        parseLineData(line, index, ctx),
    );
}

function hasLineChanges(a: ParsedLine, b: ParsedLine): boolean {
    return (
        a.accountId !== b.accountId ||
        a.counterpartyId !== b.counterpartyId ||
        a.description !== b.description ||
        JSON.stringify(a.classIds) !== JSON.stringify(b.classIds) ||
        a.debitAmount !== b.debitAmount ||
        a.creditAmount !== b.creditAmount
    );
}

export function buildLinesWithDiffs(
    log: HistoryDetailsItem,
    ctx: ResolutionContext,
): SpreadsheetData {
    const linesDiff = getDiffEntry(log.diff, "lines");

    if (!linesDiff) {
        return parseLinesArray(log.resource?.lines, ctx);
    }

    const beforeLines = parseLinesArray(linesDiff.before?.value, ctx);
    const afterLines = parseLinesArray(linesDiff.after?.value, ctx);

    if (log.changeType === AuditLogChangeType.DELETE) {
        return beforeLines.map((line) => ({ ...line, isRemoved: true }));
    }

    if (
        log.changeType === AuditLogChangeType.CREATE ||
        beforeLines.length === 0
    ) {
        return afterLines.map((line) => ({ ...line, isAdded: true }));
    }

    const beforeByOrder = new Map(beforeLines.map((l) => [l.lineOrder, l]));
    const afterByOrder = new Map(afterLines.map((l) => [l.lineOrder, l]));
    const rows: SpreadsheetData = [];

    for (const line of afterLines) {
        const oldLine = beforeByOrder.get(line.lineOrder);
        if (!oldLine) {
            rows.push({ ...line, isAdded: true });
            continue;
        }
        const changed = hasLineChanges(oldLine, line);
        rows.push({
            ...line,
            oldAccountName: oldLine.accountName,
            oldCounterpartyName: oldLine.counterpartyName,
            oldClassIds: oldLine.classIds,
            oldClassLabel: oldLine.classLabel,
            oldDebitAmount: oldLine.debitAmount,
            oldCreditAmount: oldLine.creditAmount,
            oldDescription: oldLine.description,
            hasChanges: changed,
        });
    }

    for (const oldLine of beforeLines) {
        if (!afterByOrder.has(oldLine.lineOrder)) {
            rows.push({ ...oldLine, isRemoved: true });
        }
    }

    return rows.sort(
        (a, b) => (a.lineOrder as number) - (b.lineOrder as number),
    );
}
